import {
  HttpErrorResponse,
  HttpResponseBase
} from '@angular/common/http';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormGroupName,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
} from '@angular/router';
import {
  Observable,
  Subscription, timer
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Logger } from 'src/util/logger';
import ResponseUtil from 'src/util/responseUtil';
import { PasswordService } from '../../gen/api/password.service';
import { AuthenticationChallenge } from '../../gen/model/authenticationChallenge';
import { AuthenticationFlow } from '../../gen/model/authenticationFlow';
import { AuthenticationFlows } from '../../gen/model/authenticationFlows';
import { Credentials } from '../../gen/model/credentials';
import { SendEmailForResetPasswordRequest } from '../../gen/model/sendEmailForResetPasswordRequest';
import { TimeBasedOneTimePasswordChallenge } from '../../gen/model/timeBasedOneTimePasswordChallenge';
import { TimeBasedOneTimePasswordCredential } from '../../gen/model/timeBasedOneTimePasswordCredential';
import { UserNameAndPasswordChallenge } from '../../gen/model/userNameAndPasswordChallenge';
import { UserNameAndPasswordCredential } from '../../gen/model/userNameAndPasswordCredential';
import { UserNameAndPasswordCredentialForResetPassword } from '../../gen/model/userNameAndPasswordCredentialForResetPassword';
import { AppConstants } from '../AppConstants';
import {
  AuthenticationInfo,
  AuthenticationInfoService
} from '../authentication-info.service';
import { LoginBaseComponent } from '../login-base.component';
import { StandaloneComponent } from '../standalone.component';
import { ParamsService } from '../params.service';
import {AlertHandler} from '../no-concurrent-alerts-handler';
import {animate, AnimationBuilder, AnimationFactory, query, style} from '@angular/animations';
import {
  cardFooterFeedbackTransition,
  createTabSlideUpAndFadeInTransitionSteps,
  createTabSlideDownAndFadeInTransitionSteps
} from '../animations';
import {DelegatedAuthenticationChallenge} from '../../gen';
import {PlatformLocation} from '@angular/common';
import {getApiBasePath} from '../../api/real-api/api-base-path';
import LocationUtil, {UrlParams, getAppBaseHref} from '../../util/locationUtil';
import {TranslateService} from '@ngx-translate/core';

const _ = (s) => s;


interface LoginComponentParams {
  username: string;
  resetKey: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: [
    './login.component.scss',
  ],
  viewProviders: [
    { provide: FormGroupName, useFactory: () => null },
  ],
  encapsulation: ViewEncapsulation.None,
  animations: [
    cardFooterFeedbackTransition
  ],
})
export class LoginComponent extends LoginBaseComponent implements OnInit, OnDestroy {
  private authenticationInfo: AuthenticationInfo;
  private availableFlows: AuthenticationFlows;
  private completedChallenges: Array<AuthenticationChallenge>;
  // Commented out for now as while fully functional and valid concept, not actually needed
  // private requiredChallengesInActiveFlow: Array<AuthenticationChallenge>;
  private activeFlowKey: string;
  private navigationEndSubscription: Subscription;

  private currentFlowStep: AuthenticationChallenge;

  private invalidPasswordError: UserNameAndPasswordChallenge;
  private invalidCodeError: boolean;
  private invalidTotpError: TimeBasedOneTimePasswordChallenge;
  private tooManyAttemptsError: boolean;
  private tooManyAttemptsTimeoutCounter: any;
  private tooManyAttemptsTimeout: number;
  private genericError: boolean;
  private destroyed = false;

  private totpPattern: RegExp = /^([\s]*\d[\s]*){6}$/;
  private readonly totpValidators = [Validators.required, Validators.pattern(this.totpPattern)];
  passwordStepKey = AppConstants.AC_AM_PASSWORD;
  totpStepKey = AppConstants.AC_AM_TOTP;
  userNameAsFixed = false;
  emailsent = false;
  emailfailed = false;
  showRememberMe = false;

  defaultForm = new FormGroup({
    userName: new FormControl('', [Validators.required, Validators.email]),
    rememberMe: new FormControl(false),
  });

  passwordForm = new FormGroup({
    password: new FormControl('', [Validators.required]),
  });
  resetPasswordForm = new FormGroup({
    passwordForResetPassword: new FormControl('', [Validators.required, Validators.pattern(AppConstants.PASSWORD_PATTERN)]),
    confirmPassword: new FormControl('', [Validators.required]),
    code: new FormControl('', [Validators.required]),
  });

  totpForm = new FormGroup({
    totp: new FormControl('', this.totpValidators),
  });

  @ViewChild('userName', { read: ElementRef, static: true }) userNameField: ElementRef;
  @ViewChild('password', { read: ElementRef, static: false }) passwordField: ElementRef;
  @ViewChild('passwordForResetPassword', { read: ElementRef, static: false }) passwordFieldForResetPassword: ElementRef;
  @ViewChild('codeFieldForResetPassword', { read: ElementRef, static: false }) codeFieldForResetPassword: ElementRef;
  @ViewChild('totp', { read: ElementRef, static: true }) totpField: ElementRef;
  @ViewChild('tabHolder', { static: false }) tabHolder: ElementRef;
  @ViewChild('usernameTab', { static: false }) usernameTab: ElementRef;
  @ViewChild('passwordTab', { static: false }) passwordTab: ElementRef;
  @ViewChild('totpTab', { static: false }) totpTab: ElementRef;
  @ViewChild('cardHeader', { static: false }) cardHeader: ElementRef;
  @ViewChild('restartBtn', { static: false }) restartBtn: ElementRef;

  constructor(
    private animationBuilder: AnimationBuilder,
    @Inject('PasswordApi') private passwordApi: PasswordService,
    private authnInfoService: AuthenticationInfoService,
    private paramsService: ParamsService,
    private translate: TranslateService,
    route: ActivatedRoute,
    router: Router,
    private alertHandler: AlertHandler,
    private logger: Logger,
    hostElement: ElementRef,
    private platformLocation: PlatformLocation
  ) {

    super(route, router, hostElement);
    this.focusSelectorPrefix = '.tab-content .active .form-group  ';
    this.validateConfirmPassword = this.validateConfirmPassword.bind(this);
    this.resetPasswordForm.setValidators([this.validateConfirmPassword]);
  }

  ngOnInit() {
    this.updateAuthenticationFlows((val) => {
      if (!this.destroyed) {
        if (!val) {
          return;
        } else {
          this.init();
          this.authnInfoService.startTask('login');
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.authnInfoService.sessionInfo.value.tasksInProgress.indexOf('login') >= 0) {
      this.authnInfoService.cancelTask('login');
    }
    if (this.navigationEndSubscription) {
      this.navigationEndSubscription.unsubscribe();
      this.navigationEndSubscription = undefined;
    }
  }

  protected init() {
    super.init();
    // app.component takes care of the initial loading, so we can just copy in the state from the service
    this.authenticationInfo = this.authnInfoService.authenticationInfo.getValue();
    this.availableFlows = this.authenticationInfo.authenticationFlows;
    this.completedChallenges = this.authenticationInfo.existingAuthentications;
    this.readParams().then(v => {
      if (this.completedChallenges && this.completedChallenges.length) {
        this.continueFromChallenge();
      } else if (v.username && this.defaultForm.get('userName').valid) {
        this.next();
      }
    });
    this.navigationEndSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd && e.url.indexOf(AppConstants.PATH_LOGIN) === 0) {
        const handler = () => {
          const tab: ElementRef = this.getCurrentFlowStepTab();
          this.reset();
          this.readParams().then(v => {
            if (v.username && this.defaultForm.get('userName').valid) {
              this.next(tab);
            } else {
              this.first(tab);
            }
          });
        };
        if (this.authnInfoService.sessionInfo.value.hasSession) {
          this.authnInfoService.completeTask('login');
        } else {
          handler();
        }
      }
    });
  }
  continueFromChallenge() {
    for (let i = 0; i < this.completedChallenges.length; i += 1) {
      const challenge: AuthenticationChallenge = this.completedChallenges[i];
      const un: string = this.defaultForm.get('userName').value;
      if (!un &&
          challenge.authenticationMethod === AppConstants.AC_AM_PASSWORD &&
          (challenge as UserNameAndPasswordChallenge).userName
      ) {
        this.defaultForm.get('userName').setValue((challenge as UserNameAndPasswordChallenge).userName);
      }
      const flow: AuthenticationFlow = this.findAuthenticationFlowStartingWithChallenge(
        this.availableFlows, challenge)
      ;
      if (flow) {
        this.activeFlowKey = flow.key;
        let c: AuthenticationChallenge = flow.challenges[0];
        let lastCompleted: AuthenticationChallenge = c;
        while (c && this.completedChallenges.findIndex((cc: AuthenticationChallenge) =>
          (cc.authenticationMethod !== AppConstants.AC_AM_DELEGATED && cc.authenticationMethod === c.authenticationMethod) ||
          (cc.authenticationMethod === AppConstants.AC_AM_DELEGATED &&
            (cc as DelegatedAuthenticationChallenge).applicationId ===
            (c as DelegatedAuthenticationChallenge).applicationId
          )) >= 0) {
          lastCompleted = c;
          c = c.challenges && c.challenges.length ? c.challenges[0] : null;
        }
        this.currentFlowStep = lastCompleted;
        break;
      }
    }
    this.next();
  }

  isResetPassword(): boolean {
    let retValue = false;
    if (this.router.isActive(AppConstants.PATH_RESET_PASSWORD, false)) {
      retValue = true;
    }
    return retValue;
  }

  showTotp(): boolean {
    return this.isTotpRequired();
  }

  showInvalidCodeAlert(): boolean {
    return this.invalidCodeError;
  }

  showInvalidPasswordAlert(): boolean {
    return this.invalidPasswordError !== undefined;
  }
  showTooManyAttemptsAlert(): boolean {
    return this.tooManyAttemptsError !== undefined;
  }
  showInvalidTotpAlert(): boolean {
    return this.invalidTotpError !== undefined;
  }

  showSignInFailed(): boolean {
    return this.genericError === true;
  }

  isFieldInvalid(field: AbstractControl): boolean {
    return this.hasError(field) || this.isInputInvalid(field);
  }

  isInputInvalid(field: AbstractControl): boolean {
    if (field === this.resetPasswordForm.get('confirmPassword')) {
      return (field.invalid ||
        (this.resetPasswordForm.invalid && this.resetPasswordForm.errors && this.resetPasswordForm.errors.mustMatch)) && field.touched;

    } else {
      return field.invalid && field.touched;
    }
  }

  isFieldValid(field: AbstractControl): boolean {
    return !this.hasError(field) && this.isInputValid(field);
  }

  isInputValid(field: AbstractControl): boolean {
    if (field === this.resetPasswordForm.get('confirmPassword')) {
      return field.valid
        && (this.resetPasswordForm.valid || !this.resetPasswordForm.errors || !this.resetPasswordForm.errors.mustMatch)
        && field.touched;
    } else {
      return field.valid && field.touched;
    }
  }

  hasError(field?: AbstractControl, code?: string): boolean {
    let retValue = false;
    if (field) {
      if ((field === this.defaultForm.get('userName') ||
        field === this.passwordForm.get('password') ||
        field === this.resetPasswordForm.get('passwordForResetPassword')) && this.showInvalidPasswordAlert()) {
        retValue = true;
      } else if (field === this.totpForm.get('totp') && this.showInvalidTotpAlert()) {
        retValue = true;
      } else if (field === this.resetPasswordForm.get('code') && this.showInvalidCodeAlert()) {
        retValue = true;
      }
    } else if (this.showSignInFailed()) {
      retValue = true;
    }
    return retValue;
  }
  isValidSecondaryFlow(flow: AuthenticationFlow): boolean {
    let retVal = false;
    if (flow &&
        flow.key !== (this.activeFlowKey || this.findFlowKeyByAuthenticationMethod(AppConstants.AC_AM_PASSWORD))  &&
        flow.challenges &&
        flow.challenges.length) {
      retVal = true;
    }
    return retVal;
  }
  getFlowButtonLabel(flow: AuthenticationFlow): string {
    let retVal = 'unknown';
    try {
      if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName) {
        const icon = this.getFlowIcon(flow);
        retVal = icon + (flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName;
      }
    } catch (e) {}
    return retVal;
  }
  // noinspection JSMethodCanBeStatic
  getFlowIcon(flow: AuthenticationFlow): string {
    let retVal = '';
    try {
      // TODO: refactor to use configured image and application key, once they've been merged into the DelegatedAuthenticationChallenge
      if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName) {
        if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName === 'Facebook') {
          retVal = '<span class="fab fa-fw fa-facebook-f"></span>&nbsp;';
        } else if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName === 'Google') {
          retVal = '<span class="fab fa-fw fa-google"></span>&nbsp;';
        } else if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName === 'ADFS') {
          retVal = '<span class="fab fa-fw fa-windows"></span>&nbsp;';
        }
      }
    } catch (e) {}
    return retVal;
  }

  openRegistration() {
    const ch = () => {
      const qp: any = {};
      qp[AppConstants.QP_EMAIL] = this.defaultForm.get('userName').value ? this.defaultForm.get('userName').value : null;
      this.router.navigate([AppConstants.PATH_REGISTER], {
        queryParams: qp,
        queryParamsHandling: 'merge',
      });
    };
    if (this.authnInfoService.sessionInfo.value.hasSession) {
      const sub = this.authnInfoService.endSession(true, true).subscribe(() => {
        sub.unsubscribe();
        ch();
      });
    } else {
      ch();
    }
  }
  chooseFlow(flow: AuthenticationFlow) {
    const ch = () => {
      if (flow && flow.challenges && flow.challenges.length) {
        if (flow.challenges[0].authenticationMethod === AppConstants.AC_AM_DELEGATED) {
          const challenge: DelegatedAuthenticationChallenge = flow.challenges[0] as DelegatedAuthenticationChallenge;
          // const nextUrl = this.paramsService.params.value ? this.paramsService.params.value[AppConstants.QP_NEXT] : undefined;
          const t = [
            getApiBasePath(this.platformLocation),
            '/sessions/externalIdentityProviders/',
            challenge.applicationId,
            '/signOn?next=',
            encodeURIComponent(window.location.href),
          ];
          window.location.href = t.join('');
        } else {
          // TODO: support oither types
          throw new Error('LoginComponent chooseFlow: only delegated flows are supported as secondary flows');
        }
      }
    };

    if (this.authnInfoService.sessionInfo.value.hasSession) {
      const sub = this.authnInfoService.endSession(true, true).subscribe(() => {
        sub.unsubscribe();
        ch();
      });
    } else {
      ch();
    }
  }
  // noinspection JSMethodCanBeStatic
  getFlowButtonClass(flow: AuthenticationFlow): any {
    const retVal: any = {
      btn: true,
      'btn-auto': true,
    };
    // TODO: refactor to use configured image and application key, once they've been merged into the DelegatedAuthenticationChallenge
    if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName) {
      if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName === 'Facebook') {
        retVal['btn-auto'] = false;
        retVal['btn-facebook'] = true;
      } else if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName === 'Google') {
        retVal['btn-auto'] = false;
        retVal['btn-google'] = true;
      } else if ((flow.challenges[0] as DelegatedAuthenticationChallenge).applicationName === 'ADFS') {
        retVal['btn-auto'] = false;
        retVal['btn-adfs'] = true;
      }
    }
    return retVal;
  }
  hasPassword(): boolean {
    let retVal = false;
    if (this.passwordForm.get('password').value ||
        this.resetPasswordForm.get('passwordForResetPassword').value) {
      retVal = true;
    } else if (this.completedChallenges && this.completedChallenges.length) {
      for (let i = 0; i < this.completedChallenges.length; i += 1) {
       if (this.completedChallenges[i].authenticationMethod === AppConstants.AC_AM_PASSWORD) {
         retVal = true;
         break;
       }
      }
    }
    return retVal;
  }

  first(fromTab?: ElementRef) {
    const handler = () => {
      if (!this.userNameAsFixed) {
        this.reset();
        this.updateAuthenticationFlows(() => {
          this.showStep(undefined, true, fromTab);
        }, '');
      } else {
        const flow = this.findFlowByKey(this.activeFlowKey || this.findFlowKeyByAuthenticationMethod(AppConstants.AC_AM_PASSWORD));
        this.showStep(
          this.findFirstChallengeByAuthenticationMethod(AppConstants.AC_AM_PASSWORD, flow.challenges),
          true, fromTab)
        ;
      }
    };
    if (this.authnInfoService.sessionInfo.value.hasSession) {
      const sub = this.authnInfoService.endSession(true, true).subscribe(
        () => {
          sub.unsubscribe();
          handler();
        }
      );
    } else {
      handler();
    }

  }
  findFlowKeyByAuthenticationMethod(method: string): string {
    let retVal: string = null;
    if (method && this.availableFlows && this.availableFlows.length) {
      for (let i = 0; i < this.availableFlows.length; i += 1) {
        let chall = this.availableFlows[i].challenges;
        while (chall && chall.length && chall[0]) {
          if (chall[0].authenticationMethod === method) {
            retVal = this.availableFlows[i].key;
            break;
          }
          chall = chall[0].challenges;
        }
        if (retVal) {
          break;
        }
      }
    }
    return retVal;
  }
  next(fromTab?: ElementRef) {
    this.updateAuthenticationFlows(() => {
      this.genericError = undefined;
      const anim = true;
      if (this.currentFlowStep) {
        if (!this.activeFlowKey) {
          // username screen is not a currentFlowStep and non default flows will set the key explicitly on user input,
          // so we must be here as a result of pressing next on the first challenge of the default flow. Turns out we can't
          // actually have a default flow, so we're forced to do things the hard way.
          this.activeFlowKey = this.findFlowKeyByAuthenticationMethod(this.currentFlowStep.authenticationMethod);
        }
        const challenge = this.nextChallenge(this.currentFlowStep.challenges);
        if (challenge) {
          this.showStep(challenge, anim, fromTab);
        } else {
          this.submit();
        }
      } else if (this.availableFlows) {
        this.showStep(
          this.nextChallenge(
            this.findFlowByKey(
              this.activeFlowKey || this.findFlowKeyByAuthenticationMethod(AppConstants.AC_AM_PASSWORD)
            ).challenges
          ), anim, fromTab)
        ;
      } else {
        throw new Error('LoginComponent next: No available authentication flows');
      }
    });
  }

  isLastStep(): boolean {
    return this.currentFlowStep && (!this.currentFlowStep.challenges ||
      !this.currentFlowStep.challenges.filter(
        (val) => val.authenticationMethod !== AppConstants.AC_AM_VERIFY_EMAIL).length
      )
    ;
  }

  isActiveFlowStep(name?: string): boolean {
    let retValue;
    if (this.currentFlowStep) {
      retValue = !!(name && name === this.currentFlowStep.authenticationMethod);
    } else {
      retValue = !name;
    }
    return retValue;
  }

  submit() {
    const totpRequired = this.isTotpRequired();
    const totpInputIsValid = this.hasValidTotp();
    if (!totpRequired || totpInputIsValid) {
      this.authenticate(totpRequired && totpInputIsValid);
    }
  }

  sendEmail() {
    this.emailfailed = false;
    this.emailsent = false;
    const resetUri = [];
    resetUri.push(LocationUtil.getOwnDomainURL());
    resetUri.push(getAppBaseHref(this.platformLocation));
    resetUri.push(AppConstants.ROUTE_RESET_PASSWORD);
    if (resetUri[0].indexOf('?') < 0) {
      resetUri.push('?');
    } else {
      resetUri.push('&');
    }
    resetUri.push(AppConstants.QP_RESET_PASSWORD_KEY);
    resetUri.push('={0}&');
    resetUri.push(AppConstants.QP_EMAIL);
    resetUri.push('=');
    resetUri.push('{1}');
    const params: UrlParams = LocationUtil.getParams([AppConstants.QP_EMAIL,  AppConstants.QP_RESET_PASSWORD_KEY]);
    const keys = Object.keys(params);
    for (let i = 0; i < keys.length; i += 1) {
      resetUri.push('&');
      resetUri.push(keys[i]);
      resetUri.push('=');
      resetUri.push(params[keys[i]]);
    }
    const obj: SendEmailForResetPasswordRequest = {
      userName: this.defaultForm.get('userName').value,
      resetUri: resetUri.join(''),
    };
    this.passwordApi.sendEmailForResetPassword(obj, 'response').subscribe(response => {
      this.emailsent = true;
    }, error => {
      this.emailfailed = true;
    });
  }
  showSecondaryFlows(): boolean {
    return this.availableFlows && this.availableFlows.length > 1;
    // alternative for hiding secondaries, instead of automatic call to log out on click
    // return !this.activeFlowKey && this.availableFlows && this.availableFlows.length > 1;
  }


  private reset() {
    this.activeFlowKey = undefined;
    this.currentFlowStep = undefined;
    this.defaultForm.reset();
    this.passwordForm.reset();
    this.resetPasswordForm.reset();
    this.totpForm.reset();

    this.invalidTotpError = undefined;
    this.invalidPasswordError = undefined;
    this.tooManyAttemptsError = undefined;
    this.tooManyAttemptsTimeout = undefined;
    if (this.tooManyAttemptsTimeoutCounter) {
      this.tooManyAttemptsTimeoutCounter.unsubscribe();
    }
    this.tooManyAttemptsTimeoutCounter = undefined;
    this.invalidCodeError = undefined;
    this.genericError = undefined;
    this.emailsent = false;
    this.emailfailed = false;
  }


  private readParams(anim?: boolean): Promise<LoginComponentParams> {
    return new Promise<LoginComponentParams>((resolve) => {
      const usernamePromise = this.paramsService.getParam(AppConstants.QP_EMAIL);
      const resetKeyPromise = this.paramsService.getParam(AppConstants.QP_RESET_PASSWORD_KEY);
      const invTokenPromise = this.paramsService.getParam(AppConstants.QP_INVITATION_TOKEN);
      const requirePromise = this.paramsService.getParam(AppConstants.QP_REQUIRE_CHALLENGE);
      const showRememberMePromise = this.paramsService.getParam(AppConstants.QP_SHOW_REMEMBER_ME);
      const rememberMePromise = this.paramsService.getParam(AppConstants.QP_REMEMBER_ME);

      Promise.all([
        usernamePromise,
        resetKeyPromise,
        invTokenPromise,
        requirePromise,
        showRememberMePromise,
        rememberMePromise,
      ]).then((values) => {
        const result: LoginComponentParams = {
          username: values[0],
          resetKey: values[1],
        };
        if (values[2] && values[3] === AppConstants.AC_AM_INVITATION_ACCEPTED && values[0]) {
          this.userNameAsFixed = true;
        }
        if (values[4] === 'true') {
          this.showRememberMe = true;
        } else if (values[4] === 'false') {
          this.showRememberMe = false;
        }
        if (values[5] === 'true') {
          this.defaultForm.get('rememberMe').setValue(true);
        } else if (values[5] === 'false') {
          this.defaultForm.get('rememberMe').setValue(false);
        }
        if (result.resetKey) {
          this.resetPasswordForm.get('code').setValue(result.resetKey);
        }

        if (result.username) {
          this.defaultForm.get('userName').setValue(result.username);
        }

        resolve(result);
      });
    });
  }

  private nextChallenge(challenges: Array<AuthenticationChallenge>): AuthenticationChallenge {
    return challenges && challenges.length > 0 ? this.selectChallenge(challenges[0]) : undefined;
  }

  private selectChallenge(challenge: AuthenticationChallenge): AuthenticationChallenge {
    return challenge.authenticationMethod === AppConstants.AC_AM_VERIFY_EMAIL
      ? this.nextChallenge(challenge.challenges)
      : challenge;
  }

  public getCurrentFlowStepTab(): ElementRef {
      return this.getTabByAuthenticationChallenge(this.currentFlowStep);
  }

  private getTabByAuthenticationChallenge(authc: AuthenticationChallenge): ElementRef {
    return this.getTabByAuthenticationMethod(authc ? authc.authenticationMethod : null);
  }
  private getTabByAuthenticationMethod(authm: string): ElementRef {
    let retVal: ElementRef = null;
    switch (authm) {
      case AppConstants.AC_AM_PASSWORD:
        retVal = this.passwordTab;
        break;
      case AppConstants.AC_AM_TOTP:
        retVal = this.totpTab;
        break;
      default:
        retVal = this.usernameTab;
        break;
    }
    return retVal;
  }

  private showStep(stepForChallenge?: AuthenticationChallenge, animation?: boolean, fromTab?: ElementRef) {
    const oldTab = fromTab || this.getCurrentFlowStepTab();
    const anim = animation !== false;
    this.currentFlowStep = stepForChallenge;
    const newTab = this.getCurrentFlowStepTab();
    if (!this.isIEorEDGE() && anim && newTab && oldTab && oldTab.nativeElement.id !== newTab.nativeElement.id) {
      let animationFactory: AnimationFactory;
      let headerAnimationFactory: AnimationFactory;
      if (newTab === this.usernameTab) {
        animationFactory = this.animationBuilder.build(
          createTabSlideDownAndFadeInTransitionSteps(oldTab, newTab)
        );
        if (this.restartBtn && this.restartBtn.nativeElement.className.indexOf('active') >= 0) {
          headerAnimationFactory = this.animationBuilder.build([
            query(':self', [
              style({height: this.cardHeader.nativeElement.offsetHeight + 'px'}),
              animate('200ms 0ms ease-in-out',
                style({
                  height: (this.cardHeader.nativeElement.offsetHeight - this.restartBtn.nativeElement.offsetHeight) + 'px'
                })
              ),
              style({height: 'unset'}),
            ]),
          ]);
        }
      } else {
        animationFactory = this.animationBuilder.build(
          createTabSlideUpAndFadeInTransitionSteps(oldTab, newTab)
        );
        if (this.restartBtn && this.restartBtn.nativeElement.className.indexOf('active') < 0) {
          headerAnimationFactory = this.animationBuilder.build([
            query(':self', [
              style({height: this.cardHeader.nativeElement.offsetHeight + 'px'}),
              animate('200ms 0ms ease-in-out',
                style({
                  height: (this.cardHeader.nativeElement.offsetHeight + this.restartBtn.nativeElement.offsetHeight) + 'px'
                })
              ),
              style({height: 'unset'}),
            ]),
          ]);
        }
      }
      const anm: any = animationFactory.create(this.tabHolder.nativeElement);
      anm.onDone(() => {
        anm.reset();
        StandaloneComponent.setFocusToElemOrChildControl(this.hostElement, this.focusSelectorPrefix);
      });
      if (headerAnimationFactory) {
        const hAnim = headerAnimationFactory.create(this.cardHeader.nativeElement);
        hAnim.onDone(() => {
          hAnim.reset();
        });
        hAnim.play();
      }
      anm.play();
    }
    StandaloneComponent.setFocusToElemOrChildControl(this.hostElement, this.focusSelectorPrefix);
  }


  private authenticate(withTotp: boolean) {

    this.emailfailed = false;
    this.emailsent = false;

    let userNameAndPasswordCredential = null;
    if (this.isResetPassword()) {
      userNameAndPasswordCredential = {
        authenticationMethod: AppConstants.AC_AM_RESET_PWD,
        userName: this.defaultForm.get('userName').value,
        password: this.resetPasswordForm.get('passwordForResetPassword').value,
        code: this.resetPasswordForm.get('code').value,
      } as UserNameAndPasswordCredentialForResetPassword;
    } else {
      userNameAndPasswordCredential = {
        authenticationMethod: AppConstants.AC_AM_PASSWORD,
        userName: this.defaultForm.get('userName').value,
        password: this.passwordForm.get('password').value,
      } as UserNameAndPasswordCredential;
    }

    const credentials = [userNameAndPasswordCredential] as Credentials;

    if (withTotp) {
      const totpCredential = {
        authenticationMethod: AppConstants.AC_AM_TOTP,
        oneTimePassword: this.totpForm.get('totp').value.replace(/\s/g, ''),
      } as TimeBasedOneTimePasswordCredential;
      credentials.push(totpCredential);
    }

    const remember = this.defaultForm.get('rememberMe').value;
    this.logger.debug('Add authentications, remember = %s: %O', remember, this.sanitizeCredentials(credentials));
    const addAuthenticationsRequest: Observable<HttpResponseBase> =
      this.authnInfoService.addAuthentications(credentials, remember)
        .pipe(catchError(error => ResponseUtil.handleAuthenticationChallengeResponse(error)));
    addAuthenticationsRequest.subscribe(response => {
      this.invalidPasswordError = undefined;
      this.invalidTotpError = undefined;
      this.invalidCodeError = undefined;
      this.genericError = undefined;
      this.tooManyAttemptsError = undefined;
      this.tooManyAttemptsTimeout = undefined;
      if (this.tooManyAttemptsTimeoutCounter) {
        this.tooManyAttemptsTimeoutCounter.unsubscribe();
      }
      this.tooManyAttemptsTimeoutCounter = undefined;
      const isErrorResponse = response instanceof HttpErrorResponse;
      if (isErrorResponse) {
        const erResp: HttpErrorResponse = response as HttpErrorResponse;
        this.tooManyAttemptsError = 'authentication_attempts_restricted' === erResp.error.error;
        this.invalidCodeError = 'reset_password_not_authorized' === erResp.error.error;
        this.invalidTotpError = 'invalid_code' === erResp.error.error
          ? {} as TimeBasedOneTimePasswordChallenge
          : undefined
        ;
        this.invalidPasswordError = 'invalid_username_or_password' === erResp.error.error
          ? {} as UserNameAndPasswordChallenge
          : undefined
        ;
        /** @namespace erResp.error.waitSeconds **/
        if (erResp.error.waitSeconds > 2) {
          // better to not react if less than 2 seconds, as there is not enough time for the user to read the info
          this.tooManyAttemptsTimeout = erResp.error.waitSeconds;
          this.tooManyAttemptsTimeoutCounter =  timer(1000, 1000).subscribe(() => {
            this.tooManyAttemptsTimeout--;
            if (this.tooManyAttemptsTimeout <= 0) {
              this.tooManyAttemptsTimeout = undefined;
              this.tooManyAttemptsTimeoutCounter.unsubscribe();
              this.tooManyAttemptsTimeoutCounter = undefined;
            }
          });
        }
      }
      const challenges = ResponseUtil.readChallenges(response).required;
      if (challenges && challenges.length > 0) {
        this.logger.debug('Required challenges received in authentication response: %O', challenges);
        this.invalidPasswordError = challenges.find(challenge => AppConstants.AC_AM_PASSWORD === challenge.authenticationMethod);
        if (!this.invalidTotpError) {
          this.invalidTotpError = challenges.find(challenge => AppConstants.AC_AM_TOTP === challenge.authenticationMethod);
          if (!this.invalidTotpError && this.invalidPasswordError && this.invalidPasswordError.challenges) {
            this.invalidTotpError = this.invalidPasswordError.challenges.find(
              (challenge) => AppConstants.AC_AM_TOTP === challenge.authenticationMethod);
          }
        }
      }
      if (this.invalidPasswordError || this.invalidTotpError || this.invalidCodeError || this.tooManyAttemptsError) {
        this.genericError = true;
        this.showError();
      } else {
          this.authnInfoService.completeTask('login');
      }
    }, err => {
      this.genericError = true;
    });
  }

  private showError() {
    if (this.invalidPasswordError || this.invalidCodeError) {
      this.showErrorStep(AppConstants.AC_AM_PASSWORD);
    } else if (this.invalidTotpError) {
      this.showErrorStep(AppConstants.AC_AM_TOTP);
    } else if (this.tooManyAttemptsError) {
      this.logger.debug('Too many attempts should be shown somewhere');
    }
  }

  private showErrorStep(step?: string) {
    const flow = this.findFlowByKey(this.activeFlowKey || this.findFlowKeyByAuthenticationMethod(AppConstants.AC_AM_PASSWORD));
    this.showStep(this.findFirstChallengeByAuthenticationMethod(step, flow.challenges));
  }

  private findFlowByKey(key: string): AuthenticationFlow {
    let retVal: AuthenticationFlow = null;
    if (this.availableFlows && key) {
      const flows: AuthenticationFlow[] = this.availableFlows.filter((flow) => {
        return flow.key === key;
      });
      retVal = flows.length ? flows[0] : null;
    }
    return retVal;
  }

  private findFirstChallengeByAuthenticationMethod(
    authnMethod: string,
    challenges: AuthenticationChallenge[]): AuthenticationChallenge {
    let retValue;
    if (challenges) {
      for (const challenge of challenges) {
        const match = this.findChallengeOrFirstChildChallengeByAuthenticationMethod(authnMethod, challenge);
        if (match) {
          retValue = match;
          break;
        }
      }
    }
    return retValue;
  }

  private findChallengeOrFirstChildChallengeByAuthenticationMethod(
    authnMethod: string,
    challenge: AuthenticationChallenge): AuthenticationChallenge {
    return authnMethod === challenge.authenticationMethod
      ? challenge
      : this.findFirstChallengeByAuthenticationMethod(authnMethod, challenge.challenges);
  }

  private hasValidTotp(): boolean {
    return this.totpForm.get('totp').value !== '' && this.totpForm.get('totp').valid;
  }

  private isTotpRequired(): boolean {
    let retValue;
    const activeFlow = this.findFlowByKey(this.activeFlowKey || this.findFlowKeyByAuthenticationMethod(AppConstants.AC_AM_PASSWORD));
    if (activeFlow) {
      const totpChallenge = this.findChallengeByAuthenticationMethod(activeFlow.challenges[0], AppConstants.AC_AM_TOTP);
      retValue = totpChallenge !== null;
    } else {
      retValue = false;
    }
    return retValue;
  }

  private findAuthenticationFlowStartingWithChallenge(
    authnFlows: AuthenticationFlows, authChallenge: AuthenticationChallenge): AuthenticationFlow {
    return authnFlows.find(
      flow => {
        return flow.challenges &&
               flow.challenges.length > 0 &&
               ((authChallenge.authenticationMethod !== AppConstants.AC_AM_DELEGATED &&
                 authChallenge.authenticationMethod === flow.challenges[0].authenticationMethod) ||
                 (authChallenge.authenticationMethod === AppConstants.AC_AM_DELEGATED &&
                  (authChallenge as DelegatedAuthenticationChallenge).applicationId ===
                    (flow.challenges[0] as DelegatedAuthenticationChallenge).applicationId))
        ;
      })
    ;
  }

  private findChallengeByAuthenticationMethod(challenge: AuthenticationChallenge, authenticationMethod: string): AuthenticationChallenge {
    let retValue = null;
    if (authenticationMethod === challenge.authenticationMethod) {
      retValue = challenge;
    } else if (challenge.challenges) {
      for (const childChallenge of challenge.challenges) {
        retValue = this.findChallengeByAuthenticationMethod(childChallenge, authenticationMethod);
        if (retValue) {
          break;
        }
      }
    }
    return retValue;
  }

  private updateAuthenticationFlows(callback?: (value: AuthenticationFlows) => void, overrideUserName?: string) {
    let userName = this.defaultForm.get('userName').value;
    if (overrideUserName !== undefined) {
      userName = overrideUserName;
    }
    this.logger.debug('Updating authentication status from login for: %s', userName || 'anonymous');
    this.authnInfoService.fetchAuthenticationInfo(userName, (val: AuthenticationInfo) => {
      this.authenticationInfo = val;
      this.availableFlows = this.authenticationInfo.authenticationFlows;
      this.completedChallenges = this.authenticationInfo.existingAuthentications;
      if (callback) {
        callback(this.availableFlows);
      }
    });
  }

  private validateConfirmPassword(control: AbstractControl): ValidationErrors | null {
    const pwField = this.resetPasswordForm.get('passwordForResetPassword');
    const cpwField = this.resetPasswordForm.get('confirmPassword');
    return cpwField.value === pwField.value
      ? null
      : { 'mustMatch': true };
  }

  public hasTooManyAttemptsTimeout(): boolean {
    return this.tooManyAttemptsTimeout > 0;
  }
  public resolveWindowTitlePart(): Observable<string|undefined> {
    if (this.isResetPassword()) {
      return this.translate.get(_('login.window-title.reset-password'));
    } else {
      return this.translate.get(_('login.window-title.default'));
    }
  }

}
